import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';  // Correct import

const STORE_API_URL = process.env.REACT_APP_API_URL;

export const AuthContext = createContext();

// Function to check if the token is expired
const isTokenExpired = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Current time in seconds
    return decodedToken.exp < currentTime; // Check if token is expired
  } catch (error) {
    console.error('Token decoding failed', error);
    return true; // If there's an error, consider the token expired
  }
};

// Function to decode the role from the token
const getRoleFromToken = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.role || 'user'; // Default to 'user' if role is not present
  } catch (error) {
    console.error('Failed to decode role from token', error);
    return 'user'; // Default role
  }
};

// Function to decode the name from the token
const getNameFromToken = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.name || ''; // Default to empty string if name is not present
  } catch (error) {
    console.error('Failed to decode name from token', error);
    return ''; // Default to empty string
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [role, setRole] = useState('user');
  const [name, setName] = useState(''); // Add state for name
  const navigate = useNavigate();

  // Function to validate the token
  const validateToken = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    const activeToken = token || storedToken;  // Use token from state or storage
    
    if (!activeToken || isTokenExpired(activeToken)) {
      setIsAuthenticated(false);
      setToken(null);
      setRole('user'); // Reset role
      setName(''); // Reset name
      localStorage.removeItem('token'); // Clear token from local storage
      localStorage.removeItem('selectedStore'); 
      return false;
    }

    // Update the role and name when validating the token
    setRole(getRoleFromToken(activeToken));
    setName(getNameFromToken(activeToken)); // Set the name
    return true;
  }, [token]);

  // Check authentication status when the component mounts
  useEffect(() => {
    // console.log('AuthContext useEffect triggered'); 

    const checkAuthStatus = async () => {
      // console.log('checkAuthStatus called');

      const storedToken = localStorage.getItem('token');
      // console.log('Stored Token:', storedToken);

      if (storedToken && !isTokenExpired(storedToken)) {
        setToken(storedToken);
        setIsAuthenticated(true);
        setRole(getRoleFromToken(storedToken));
        setName(getNameFromToken(storedToken)); // Set the name
        return; // Token is valid, no need to call `/auth/status`
      }

      // console.log('Calling /auth/status API...');

      try {
        const response = await fetch(`${STORE_API_URL}/auth/status`, {
          credentials: 'include',
        });
        const data = await response.json();
        setIsAuthenticated(data.is_authenticated);
        console.log('Auth Status Response: ', JSON.stringify(data));
        if (data.is_authenticated) {
          console.log('Authenticated and token is set: ' + data.token);
          setToken(data.token); // Ensure token is set
          localStorage.setItem('token', data.token); // Save token in local storage
          setRole(getRoleFromToken(data.token)); // Decode and set the role
          setName(getNameFromToken(data.token)); // Decode and set the name
        }
      } catch (error) {
        console.error('Failed to check authentication status', error);
        setIsAuthenticated(false);
        setToken(null); // Ensure token is cleared
        setRole('user'); // Reset role
        setName(''); // Reset name
        localStorage.removeItem('token'); // Clear token from local storage
        localStorage.removeItem('selectedStore'); 
        navigate('/'); // Redirect to home if an error occurs
      }
    };

    checkAuthStatus();
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated, token, setToken, role, name, validateToken }}>
      {children}
    </AuthContext.Provider>
  );
};
